import React, { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowSquareOut, Bank, CalendarBlank, CurrencyEur, Database, DownloadSimple,
  FileText, GlobeHemisphereWest, MagnifyingGlass, MapPin, Plus, SealCheck,
  ShieldCheck, WarningCircle, X,
} from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import BankOpportunityForm from "@/components/BankOpportunityForm";

const fieldClass =
  "h-11 border border-slate-200 bg-white px-3 text-sm outline-none focus:border-[#C5A059]";

const titleCase = (value) =>
  String(value || "—").replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());

function Badge({ children, tone = "slate" }) {
  const tones = {
    slate: "border-slate-200 bg-slate-50 text-slate-600",
    gold: "border-[#C5A059]/40 bg-[#C5A059]/10 text-[#8A692D]",
    green: "border-emerald-200 bg-emerald-50 text-emerald-700",
    amber: "border-amber-200 bg-amber-50 text-amber-700",
    blue: "border-blue-200 bg-blue-50 text-blue-700",
  };
  return (
    <span className={`inline-flex border px-2 py-1 text-[10px] font-semibold uppercase tracking-wider ${tones[tone]}`}>
      {children}
    </span>
  );
}

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-white border border-slate-200 p-5 flex items-center gap-4">
      <div className={`w-11 h-11 flex items-center justify-center ${accent ? "bg-[#C5A059]/12 text-[#A77E35]" : "bg-[#0B2545]/8 text-[#0B2545]"}`}>
        <Icon size={22} />
      </div>
      <div>
        <div className="text-2xl font-serif text-[#0B2545]">{value}</div>
        <div className="text-xs text-slate-500">{label}</div>
      </div>
    </div>
  );
}

function DiscoveryCard({ candidate, importing, onImport, t, language }) {
  const price = candidate.base_price
    ? new Intl.NumberFormat(language, {
        style: "currency",
        currency: candidate.currency || "EUR",
        maximumFractionDigits: 0,
      }).format(candidate.base_price)
    : t("bankOpportunities.priceUponRequest");

  return (
    <article className="border border-slate-200 bg-white p-5">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <div className="mb-2 flex flex-wrap gap-2">
            <Badge tone="blue">
              {t("bankOpportunities.discovery.externalWeb", "External / Web")}
            </Badge>
            <Badge tone="amber">
              {t("bankOpportunities.discovery.unverified", "Unverified")}
            </Badge>
          </div>
          <h3 className="font-serif text-xl leading-tight text-[#0B2545]">
            {candidate.title}
          </h3>
        </div>
        <span className="whitespace-nowrap font-serif text-lg text-[#0B2545]">
          {price}
        </span>
      </div>

      <p className="line-clamp-3 text-sm leading-6 text-slate-600">
        {candidate.summary || candidate.description}
      </p>

      <div className="mt-4 grid grid-cols-2 gap-3 border-y border-slate-100 py-3 text-xs">
        <div>
          <span className="block text-slate-400">
            {t("bankOpportunities.discovery.source", "Source")}
          </span>
          <span className="text-[#0B2545]">{candidate.source_name}</span>
        </div>
        <div>
          <span className="block text-slate-400">
            {t("bankOpportunities.discovery.reference", "Reference")}
          </span>
          <span className="text-[#0B2545]">
            {candidate.external_reference || "—"}
          </span>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={() => window.open(candidate.source_url, "_blank", "noopener,noreferrer")}
          className="h-9 rounded-none text-xs"
        >
          <ArrowSquareOut size={15} className="mr-2" />
          {t("bankOpportunities.discovery.viewOriginal", "View original")}
        </Button>
        <Button
          type="button"
          disabled={candidate.already_imported || importing}
          onClick={() => onImport(candidate)}
          className="h-9 rounded-none bg-[#C5A059] text-xs text-white hover:bg-[#A77E35]"
        >
          <DownloadSimple size={15} className="mr-2" />
          {candidate.already_imported
            ? t("bankOpportunities.discovery.imported", "Already imported")
            : t("bankOpportunities.discovery.import", "Import to Bank & Distressed")}
        </Button>
      </div>
    </article>
  );
}

function OpportunityCard({ record, onEdit, onLegal, onArchive, t, language }) {
  const amount = record.minimum_price ?? record.base_price;
  const price = amount
    ? new Intl.NumberFormat(language, {
        style: "currency",
        currency: record.currency || "EUR",
        maximumFractionDigits: 0,
      }).format(amount)
    : t("bankOpportunities.priceUponRequest");

  return (
    <article
      onClick={() => onEdit(record)}
      className="group bg-white border border-slate-200 cursor-pointer hover:-translate-y-0.5 hover:shadow-[0_16px_45px_rgba(11,37,69,0.09)] transition-all"
      data-testid={`bank-opportunity-${record.id}`}
    >
      <div className="h-1 bg-gradient-to-r from-[#0B2545] via-[#C5A059] to-[#0B2545]" />
      <div className="p-5">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#A77E35] mb-1">
              {record.external_reference || t(`bankOpportunities.form.options.${record.opportunity_type}`, titleCase(record.opportunity_type))}
            </p>
            <h3 className="font-serif text-xl leading-tight text-[#0B2545] group-hover:text-[#A77E35] transition-colors">
              {record.title}
            </h3>
            <p className="text-sm text-slate-500 mt-1">
              {record.bank_or_creditor || record.source_name || t(`bankOpportunities.form.options.${record.source_type}`, titleCase(record.source_type))}
            </p>
          </div>
          <Badge tone={record.record_status === "published" ? "green" : "gold"}>
            {t(`bankOpportunities.form.options.${record.record_status}`, titleCase(record.record_status))}
          </Badge>
        </div>

        <div className="grid grid-cols-2 gap-3 py-4 border-y border-slate-100">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-400">
              {t("bankOpportunities.price")}
            </div>
            <div className="font-serif text-lg text-[#0B2545] mt-1">{price}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-400">
              {t("bankOpportunities.discount")}
            </div>
            <div className="font-serif text-lg text-[#0B2545] mt-1">
              {record.estimated_discount_percent != null
                ? `${record.estimated_discount_percent}%`
                : "—"}
            </div>
          </div>
        </div>

        <div className="mt-4 space-y-2 text-sm text-slate-600">
          <div className="flex items-center gap-2">
            <MapPin size={16} className="text-[#C5A059]" />
            <span>{[record.city, record.district, record.country].filter(Boolean).join(", ") || "—"}</span>
          </div>
          <div className="flex items-center gap-2">
            <CalendarBlank size={16} className="text-[#C5A059]" />
            <span>{record.deadline || "—"}</span>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mt-4">
          <Badge tone={record.verification_status === "verified" ? "green" : "amber"}>
            {t(`bankOpportunities.form.options.${record.verification_status}`, titleCase(record.verification_status))}
          </Badge>
          <Badge tone={record.commission_status === "protected" ? "blue" : "slate"}>
            {t(`bankOpportunities.form.options.${record.commission_status}`, titleCase(record.commission_status))}
          </Badge>
        </div>
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onLegal(record);
          }}
          className="mt-4 w-full border border-[#0B2545]/20 px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-[#0B2545] hover:border-[#C5A059] hover:text-[#A77E35] transition-colors flex items-center justify-center gap-2"
          data-testid={`legal-documents-${record.id}`}
        >
          <FileText size={17} />
          {t("legalDocuments.navigation", "Legal documents")}
        </button>
        {record.record_status === "draft" && (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onArchive(record);
            }}
            className="mt-2 w-full border border-red-200 px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-red-700 hover:bg-red-50"
          >
            {t("bankOpportunities.archiveDraft", "Archive draft")}
          </button>
        )}
      </div>
    </article>
  );
}

export default function BankOpportunities() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [source, setSource] = useState("all");
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showDiscovery, setShowDiscovery] = useState(false);
  const [discoveryLocation, setDiscoveryLocation] = useState("Portugal");
  const [discovery, setDiscovery] = useState(null);
  const [importingId, setImportingId] = useState(null);

  const { data: meta } = useQuery({
    queryKey: ["bank-opportunities-meta"],
    queryFn: async () => (await api.get("/bank-opportunities/meta")).data,
  });

  const params = {};
  if (q.trim()) params.q = q.trim();
  if (status !== "all") params.status = status;
  if (source !== "all") params.source_type = source;

  const { data, isLoading } = useQuery({
    queryKey: ["bank-opportunities", params],
    queryFn: async () => (await api.get("/bank-opportunities", { params })).data,
  });

  const records = useMemo(() => data?.results || [], [data]);

  const stats = useMemo(() => ({
    total: data?.total || 0,
    published: records.filter((item) => item.record_status === "published").length,
    verified: records.filter((item) => item.verification_status === "verified").length,
    protected: records.filter((item) => item.commission_status === "protected").length,
  }), [data, records]);

  const archive = useMutation({
    mutationFn: async (item) => api.delete(`/bank-opportunities/${item.id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bank-opportunities"] });
      toast.success(t("bankOpportunities.draftArchived", "Draft archived"));
    },
    onError: (error) => toast.error(error.response?.data?.detail || t("actionFailed", "Action failed")),
  });

  const archiveDraft = (item) => {
    if (window.confirm(`Archive draft "${item.title}"? It will be removed from the active list.`)) {
      archive.mutate(item);
    }
  };

  const save = useMutation({
    mutationFn: async (payload) => {
      if (editing?.id) {
        return (await api.put(`/bank-opportunities/${editing.id}`, payload)).data;
      }
      return (await api.post("/bank-opportunities", payload)).data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bank-opportunities"] });
      toast.success(t("saved", "Saved successfully"));
      setEditing(null);
      setShowForm(false);
    },
    onError: (error) => {
      const detail = error?.response?.data?.detail;
      const message = typeof detail === "string"
        ? detail
        : detail?.message || t("actionFailed", "Action failed");
      toast.error(message);
    },
  });

  const discover = useMutation({
    mutationFn: async () => (
      await api.post("/bank-opportunities/web/discover", {
        location: discoveryLocation.trim() || "Portugal",
      })
    ).data,
    onSuccess: (result) => {
      setDiscovery(result);
      if (!result.configured) {
        toast.error(t(
          "bankOpportunities.discovery.notConfigured",
          "No web-search provider is configured."
        ));
      } else if (!result.count) {
        toast.info(t(
          "bankOpportunities.discovery.noneFound",
          "No new matching opportunities were found."
        ));
      }
    },
    onError: (error) => {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t("actionFailed", "Action failed"));
    },
  });

  const importCandidate = useMutation({
    mutationFn: async (candidate) => {
      setImportingId(candidate.id);
      return (await api.post("/bank-opportunities/web/import", { candidate })).data;
    },
    onSuccess: (_, candidate) => {
      setDiscovery((current) => ({
        ...current,
        results: (current?.results || []).map((item) => (
          item.id === candidate.id ? { ...item, already_imported: true } : item
        )),
      }));
      queryClient.invalidateQueries({ queryKey: ["bank-opportunities"] });
      toast.success(t(
        "bankOpportunities.discovery.importSuccess",
        "Opportunity imported as an unverified draft."
      ));
    },
    onError: (error) => {
      const detail = error?.response?.data?.detail;
      const message = typeof detail === "string"
        ? detail
        : detail?.message || t("actionFailed", "Action failed");
      toast.error(message);
    },
    onSettled: () => setImportingId(null),
  });

  const openNew = () => {
    setEditing(null);
    setShowForm(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    setShowForm(true);
  };

  return (
    <div className="px-4 py-7 sm:px-7 lg:px-10 lg:py-9 max-w-[1600px] mx-auto">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-5 mb-8">
        <div>
          <p className="text-[11px] uppercase tracking-[0.24em] text-[#A77E35] mb-2">
            Panatlantik Investment Intelligence
          </p>
          <h1 className="font-serif text-3xl sm:text-4xl text-[#0B2545]">
            {t("bankOpportunities.title")}
          </h1>
          <p className="text-slate-500 mt-2 max-w-3xl">
            {t("bankOpportunities.subtitle")}
          </p>
        </div>
        <div className="flex flex-col flex-wrap gap-3 sm:flex-row">
            {user?.role === "admin" && (
              <Button
                variant="outline"
                onClick={() => setShowDiscovery((value) => !value)}
                className="h-12 rounded-none border-[#C5A059] px-6 text-[#0B2545] hover:bg-[#C5A059]/10"
                data-testid="discover-bank-opportunities"
              >
                <GlobeHemisphereWest size={18} className="mr-2" />
                {t("bankOpportunities.discovery.button", "Discover opportunities")}
              </Button>
            )}
          <Button
            variant="outline"
            onClick={() => navigate("/bank-opportunities/sources")}
            className="h-12 rounded-none border-[#0B2545] px-6 text-[#0B2545] hover:bg-[#0B2545]/5"
            data-testid="bank-opportunity-sources"
          >
            <Database size={18} className="mr-2" />
            {t("bankOpportunities.sourceRegistry.navigation")}
          </Button>
          <Button
            onClick={openNew}
            className="h-12 rounded-none bg-[#0B2545] px-6 hover:bg-[#12345e]"
            data-testid="add-bank-opportunity"
          >
            <Plus size={18} className="mr-2" />
            {t("bankOpportunities.add")}
          </Button>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
        <Stat icon={Bank} label={t("bankOpportunities.total")} value={stats.total} />
        <Stat icon={SealCheck} label={t("bankOpportunities.published")} value={stats.published} />
        <Stat icon={WarningCircle} label={t("bankOpportunities.verified")} value={stats.verified} />
        <Stat icon={ShieldCheck} label={t("bankOpportunities.protected")} value={stats.protected} accent />
      </div>

      <div className="bg-white border border-slate-200 p-3 mb-6 grid lg:grid-cols-[1fr_220px_220px] gap-3">
        <div className="relative">
          <MagnifyingGlass size={19} className="absolute left-3 top-3 text-slate-400" />
          <Input
            value={q}
            onChange={(event) => setQ(event.target.value)}
            placeholder={t("bankOpportunities.search")}
            className="rounded-none h-11 pl-10 border-slate-200"
          />
        </div>
        <select value={status} onChange={(event) => setStatus(event.target.value)} className={fieldClass}>
          <option value="all">{t("bankOpportunities.allStatuses")}</option>
          {(meta?.record_statuses || []).map((value) => (
            <option key={value} value={value}>{t(`bankOpportunities.form.options.${value}`, titleCase(value))}</option>
          ))}
        </select>
        <select value={source} onChange={(event) => setSource(event.target.value)} className={fieldClass}>
          <option value="all">{t("bankOpportunities.allSources")}</option>
          {(meta?.source_types || []).map((value) => (
            <option key={value} value={value}>{t(`bankOpportunities.form.options.${value}`, titleCase(value))}</option>
          ))}
        </select>
      </div>

      <div className="border-l-2 border-[#C5A059] bg-[#0B2545]/[0.035] px-4 py-3 mb-6 text-xs text-slate-600">
        {t("bankOpportunities.notice")}
      </div>

      {showDiscovery && user?.role === "admin" && (
        <section className="mb-7 border border-[#C5A059]/40 bg-[#C5A059]/[0.035]">
          <div className="flex items-start justify-between gap-4 border-b border-[#C5A059]/20 p-5">
            <div>
              <h2 className="font-serif text-2xl text-[#0B2545]">
                {t("bankOpportunities.discovery.title", "Automatic opportunity discovery")}
              </h2>
              <p className="mt-1 max-w-3xl text-sm text-slate-600">
                {t(
                  "bankOpportunities.discovery.subtitle",
                  "Search approved public bank and distressed-property sources. Every result requires review before import."
                )}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowDiscovery(false)}
              className="text-slate-400 hover:text-[#0B2545]"
              aria-label={t("close", "Close")}
            >
              <X size={20} />
            </button>
          </div>

          <div className="grid gap-3 p-5 sm:grid-cols-[1fr_auto]">
            <Input
              value={discoveryLocation}
              onChange={(event) => setDiscoveryLocation(event.target.value)}
              placeholder={t("bankOpportunities.discovery.location", "Portugal, district or city")}
              className="h-11 rounded-none border-slate-200 bg-white"
            />
            <Button
              type="button"
              disabled={discover.isPending}
              onClick={() => discover.mutate()}
              className="h-11 rounded-none bg-[#0B2545] px-6 hover:bg-[#12345e]"
            >
              <MagnifyingGlass size={17} className="mr-2" />
              {discover.isPending
                ? t("bankOpportunities.discovery.searching", "Searching…")
                : t("bankOpportunities.discovery.search", "Search approved sources")}
            </Button>
          </div>

          {discovery?.configured && (
            <div className="border-t border-[#C5A059]/20 px-5 py-3 text-xs text-slate-500">
              {t("bankOpportunities.discovery.provider", "Provider")}:{" "}
              <strong>{discovery.provider}</strong>
              {" · "}
              {t("bankOpportunities.discovery.results", "Results")}:{" "}
              <strong>{discovery.count}</strong>
              {discovery.checked_at && (
                <>{" · "}{new Date(discovery.checked_at).toLocaleString()}</>
              )}
            </div>
          )}

          {discovery?.configured && discovery.sources?.length > 0 && (
            <div className="border-t border-[#C5A059]/20 px-5 py-3 text-sm text-slate-600">
              <strong>Results by source</strong>
              <div className="mt-2 grid gap-2 sm:grid-cols-2">
                {discovery.sources.map((item) => (
                  <div key={item.id} className="border border-slate-200 bg-white px-3 py-2">
                    {item.name}: {item.raw_count ?? 0} found, {item.count ?? 0} accepted
                    {" · "}{item.status}
                  </div>
                ))}
              </div>
            </div>
          )}

          {discovery?.configured && discovery.results?.length === 0 && (
            <div className="border-t border-[#C5A059]/20 p-8 text-center text-sm text-slate-500">
              No verified property listings found. Check the search pages below for possible listings.
            </div>
          )}

          {discovery?.review_pages?.length > 0 && (
            <details className="border-t border-[#C5A059]/20 px-5 py-4 text-sm">
              <summary className="cursor-pointer font-semibold text-[#0B2545]">
                Review search pages ({discovery.review_pages.length})
              </summary>
              <p className="mt-2 text-slate-600">
                These pages did not pass the property listing checks. Open the original site to verify them; they cannot be imported as property cards here.
              </p>
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                {discovery.review_pages.map((page) => (
                  <a key={page.url} href={page.url} target="_blank" rel="noopener noreferrer" className="border border-slate-200 bg-white p-3 text-[#0B2545] hover:underline break-words">
                    <span className="block text-xs text-slate-500">{page.source}</span>
                    {page.title}
                  </a>
                ))}
              </div>
            </details>
          )}

          {discovery?.results?.length > 0 && (
            <div className="grid gap-4 border-t border-[#C5A059]/20 p-5 lg:grid-cols-2">
              {discovery.results.map((candidate) => (
                <DiscoveryCard
                  key={candidate.id}
                  candidate={candidate}
                  importing={importCandidate.isPending && importingId === candidate.id}
                  onImport={(item) => importCandidate.mutate(item)}
                  t={t}
                  language={i18n.language}
                />
              ))}
            </div>
          )}
        </section>
      )}

      {isLoading ? (
        <div className="flex justify-center py-24">
          <div className="animate-spin h-8 w-8 border-2 border-[#0B2545] border-t-transparent rounded-full" />
        </div>
      ) : records.length === 0 ? (
        <div className="bg-white border border-slate-200 text-center py-20">
          <Bank size={42} className="mx-auto text-slate-300 mb-4" />
          <p className="text-slate-500">{t("bankOpportunities.noResults")}</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
          {records.map((record) => (
            <OpportunityCard
              key={record.id}
              record={record}
              onEdit={openEdit}
              onArchive={archiveDraft}
              onLegal={(item) => navigate(`/bank-opportunities/${item.id}/legal`)}
              t={t}
              language={i18n.language}
            />
          ))}
        </div>
      )}

      {showForm && (
        <BankOpportunityForm
          initial={editing}
          meta={meta}
          saving={save.isPending}
          onSave={(payload) => save.mutate(payload)}
          onClose={() => {
            setShowForm(false);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}
